/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import LangSwitch from "@/components/LangSwitch";

export default function Navbar() {
  const { t } = useLang();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.features, href: "#features" },
    { label: t.trainer.school, href: "#trainer" },
    { label: t.nav.pricing, href: "#pricing" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contact, href: "#contact" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAFAF7]/85 backdrop-blur-xl border-b border-[#E2E4DF]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-logo"
          className="flex items-center gap-2 group"
        >
          <span className="w-8 h-8 rounded-full bg-[#1C3A27] grid place-items-center">
            <span className="w-3 h-3 rounded-full bg-[#E05A3D]" />
          </span>
          <span className="font-display text-xl font-bold tracking-tight">
            {t.brand.name}
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="text-sm font-medium text-[#5C6B5C] hover:text-[#1C3A27] transition-colors relative group"
            >
              <span className={l.redacted ? "redacted" : ""}>{l.label}</span>
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#E05A3D] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangSwitch variant="light" />
          {user ? (
            <Link
              to={user.role === "owner" ? "/dashboard/owner" : user.role === "trainer" ? "/dashboard/trainer" : "/dashboard/client"}
              data-testid="nav-dashboard-link"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#1C3A27] text-white text-sm font-semibold hover:bg-[#142A1C] transition-colors"
            >
              <LogIn size={14} /> Mon espace
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border-2 border-[#1C3A27] bg-white text-[#1C3A27] text-sm font-semibold hover:bg-[#1C3A27] hover:text-white transition-colors"
              >
                <LogIn size={14} /> Espace client
              </Link>
              <Link
                to="/signup"
                data-testid="nav-signup-link"
                className="inline-flex items-center h-10 px-5 rounded-full bg-[#E05A3D] hover:bg-[#C84B31] text-white text-sm font-semibold transition-colors shadow-md shadow-[#E05A3D]/20"
              >
                Créer mon compte
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LangSwitch variant="light" />
          <button
            data-testid="mobile-menu-toggle"
            className="p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[#E2E4DF] bg-[#FAFAF7]"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[#1C3A27]"
              >
                <span className={l.redacted ? "redacted" : ""}>{l.label}</span>
              </a>
            ))}
            <div className="border-t border-[#E2E4DF] pt-4 flex flex-col gap-3">
              {user ? (
                <Link
                  to={user.role === "owner" ? "/dashboard/owner" : user.role === "trainer" ? "/dashboard/trainer" : "/dashboard/client"}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-dashboard"
                  className="inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#1C3A27] text-white text-base font-semibold"
                >
                  <LogIn size={16} /> Mon espace
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-login"
                    className="inline-flex items-center justify-center gap-2 h-12 rounded-full border-2 border-[#1C3A27] bg-white text-[#1C3A27] text-base font-semibold"
                  >
                    <LogIn size={16} /> Espace client
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-signup"
                    className="inline-flex items-center justify-center h-12 rounded-full bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold shadow-md shadow-[#E05A3D]/20"
                  >
                    Créer mon compte
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
